import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, MapPin, Wallet, X } from 'lucide-react';
import { useState } from 'react';

import { VideoPlayer } from '@/components/kibo-ui/video-player/video-player';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { describeGraphicFailure } from '@/features/graphics/graphic-failure';
import { getAdQueryOptions } from '@/features/meta-ads/api/get-ad/get-ad.hook';
import { useArtifactPanel } from './artifact-panel-context';
import { GraphicCarousel } from './graphic-carousel';
import { ImageReveal } from './image-reveal';
import { AdCardEditButton } from './rich/ad-card-edit-button';
import { AdCardEditTargetContext } from './rich/ad-card-edit-context';
import { describeAdStatus, formatCta } from './rich/ad-preview-card';
import {
  savedCampaignBudget,
  savedCampaignTargeting,
  useSavedCampaign,
} from './rich/use-saved-ad-preview';
import { creativeSlides, useCreativeAsset } from './use-creative-asset';

/**
 * A saved ad at full size: the creative, then the copy around it.
 *
 * The media panel shows a video or a graphic; this shows an AD, which is one
 * of those plus a name, a headline, the primary text and where it sends
 * people. It reads the saved ad rather than the tool output that announced
 * it, so a rename or a caption edit made in a later turn — or in another tab
 * — is what the panel shows, the same way the chat card already does.
 *
 * The creative is polled through the same query key the row and the media
 * panel use, so a graphic still rendering when the ad is opened fills in here
 * without a second request stream.
 */
export function AdArtifactPanel({
  adId,
  onSendMessage,
  closable = true,
}: {
  adId: string;
  /** Lets the copy's edit pencils send a request; absent, they are hidden. */
  onSendMessage?: (text: string) => void;
  closable?: boolean;
}) {
  const { close } = useArtifactPanel();
  const [slide, setSlide] = useState(0);

  const {
    data: ad,
    isPending: adPending,
    isError: adError,
    refetch,
  } = useQuery({
    ...getAdQueryOptions(adId),
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
  });

  // What to show, and whether it is still being made.
  //
  // A graphic is always read from the graphics endpoint, even once the ad
  // carries its image URL: a carousel is several images and the ad only
  // keeps one. A video the ad already resolved to a file (an upload, or a
  // finished render) is shown from that; only an unfinished render is polled.
  const uploadedImage =
    ad?.video?.mediaType === 'image'
      ? (ad.video.videoUrl ?? ad.video.thumbnailUrl ?? null)
      : null;
  const resolvedVideo =
    ad?.video && ad.video.mediaType !== 'image' && ad.video.videoUrl
      ? { url: ad.video.videoUrl, poster: ad.video.thumbnailUrl ?? undefined }
      : null;
  const creative =
    ad?.graphicId && !uploadedImage
      ? { kind: 'graphic' as const, id: ad.graphicId }
      : ad?.videoId && !uploadedImage && !resolvedVideo
        ? { kind: 'video' as const, id: ad.videoId }
        : null;

  const { asset, pending: rendering, failed } = useCreativeAsset(creative);
  const slides = creativeSlides(asset);
  const graphicFailure =
    failed && creative?.kind === 'graphic'
      ? describeGraphicFailure(asset ?? {}, 'Ask Claire to try it again.')
      : null;
  const isVideoAd = creative?.kind === 'video' || resolvedVideo !== null;
  const imageSrc =
    slides.length === 1
      ? slides[0].url
      : (uploadedImage ??
        (slides.length === 0 ? ad?.graphicImageUrl : undefined) ??
        undefined);

  const campaign = useSavedCampaign(ad?.metaCampaignId);
  const budget = campaign ? savedCampaignBudget(campaign) : undefined;
  const targeting = campaign ? savedCampaignTargeting(campaign) : undefined;
  const status = ad
    ? describeAdStatus({ variant: 'draft', savedAdStatus: ad.status })
    : null;

  const copy = ad
    ? [
        {
          field: 'headline',
          value: ad.headline ?? undefined,
          className: 'text-sm font-semibold leading-snug',
        },
        {
          field: 'caption / ad copy',
          value: ad.primaryText ?? undefined,
          className:
            'whitespace-pre-line text-xs leading-relaxed text-muted-foreground',
        },
        {
          field: 'description',
          value: ad.description ?? undefined,
          className: 'whitespace-pre-line text-xs text-muted-foreground',
        },
        {
          field: 'call to action',
          value: ad.callToAction ? formatCta(ad.callToAction) : undefined,
          className: 'text-xs',
        },
      ]
    : [];

  return (
    <AdCardEditTargetContext.Provider value={adId}>
      <div
        className="flex h-full min-h-0 flex-col bg-muted/30"
        data-testid="ad-artifact-panel"
      >
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
          <p className="min-w-0 flex-1 truncate text-sm font-medium">
            {ad?.name ?? 'Ad preview'}
          </p>
          {status ? (
            <Badge variant={status.variant} className="shrink-0 text-[10px]">
              {status.label}
            </Badge>
          ) : null}
          {closable ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={close}
              aria-label="Close panel"
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto p-4">
          {adError ? (
            <div
              className="flex flex-col items-center gap-2 text-center"
              role="alert"
            >
              <p className="text-sm">The ad could not be loaded.</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </div>
          ) : (
            <>
              {/* The creative's own pencil sits over the picture, as it did
                  on the card: it sends "change the creative" for THIS ad
                  through replaceAdCreative. Without it the only route to a
                  new picture was the graphic itself, which edits the PNG as
                  content and leaves the ad on the old version. */}
              <div className="relative flex w-full max-w-sm justify-center">
                {ad && !adPending && !failed ? (
                  <div className="absolute right-2 top-2 z-10 rounded-sm bg-background/90">
                    <AdCardEditButton
                      adName={ad.name}
                      field="creative"
                      onSendMessage={onSendMessage}
                    />
                  </div>
                ) : null}
                {failed ? (
                  <div
                    className="flex flex-col items-center gap-2 py-8 text-center"
                    role="alert"
                  >
                    <AlertTriangle className="size-6 text-destructive" />
                    <p className="text-sm font-medium">
                      {graphicFailure?.isSafetyBlock
                        ? graphicFailure.title
                        : 'That render failed.'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {graphicFailure?.message ??
                        asset?.errorMessage ??
                        'Ask Claire to try it again.'}
                    </p>
                  </div>
                ) : !adPending &&
                  !rendering &&
                  (resolvedVideo || asset?.blobUrl) ? (
                  <div className="flex max-h-full w-full animate-in items-center justify-center fade-in-0 duration-500 motion-reduce:animate-none">
                    <VideoPlayer
                      key={adId}
                      fitToMedia
                      src={resolvedVideo?.url ?? asset?.blobUrl ?? ''}
                      poster={
                        resolvedVideo?.poster ??
                        asset?.thumbnailUrl ??
                        undefined
                      }
                      className="max-h-full w-auto"
                    />
                  </div>
                ) : !adPending && !rendering && slides.length > 1 ? (
                  <div className="w-full animate-in fade-in-0 duration-500 motion-reduce:animate-none">
                    <GraphicCarousel
                      slides={slides}
                      index={slide}
                      onSelect={setSlide}
                    />
                  </div>
                ) : adPending || rendering || imageSrc ? (
                  /* ONE frame from "loading the ad" through "rendering the
                     creative" to the finished picture. Each of those used to
                     be its own element, so the panel jumped twice before the
                     image popped in; now the frame stays put and the image
                     fills it, blurred and then sharp. */
                  <ImageReveal
                    src={adPending || rendering ? undefined : imageSrc}
                    previewSrc={
                      adPending || rendering
                        ? undefined
                        : slides[0]?.thumbnailUrl
                    }
                    alt={ad?.name ?? ''}
                    aspectRatio={
                      asset?.aspectRatio ?? (isVideoAd ? '9:16' : '1:1')
                    }
                    caption={
                      adPending
                        ? undefined
                        : asset?.processingStage
                          ? `${asset.processingStage}…`
                          : isVideoAd
                            ? 'Rendering — usually a minute or two.'
                            : 'Rendering — images can take a few minutes.'
                    }
                    progress={asset?.progress}
                  />
                ) : (
                  <p className="py-8 text-center text-xs text-muted-foreground">
                    Nothing to show for this one yet.
                  </p>
                )}
              </div>

              {ad ? (
                <div className="w-full max-w-sm space-y-1.5 rounded-lg border bg-card p-3">
                  {copy.map(({ field, value, className }) =>
                    value || onSendMessage ? (
                      <div
                        key={field}
                        className="flex items-start justify-between gap-2"
                      >
                        <p className={`min-w-0 break-words pt-1 ${className}`}>
                          {value || `Add ${field}`}
                        </p>
                        <AdCardEditButton
                          adName={ad.name}
                          field={field}
                          onSendMessage={onSendMessage}
                        />
                      </div>
                    ) : null
                  )}
                  {campaign || budget || targeting || ad.destinationUrl ? (
                    <div className="mt-2 space-y-1 border-t pt-2">
                      {campaign ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Badge variant="secondary" className="text-[10px]">
                            {campaign.name}
                          </Badge>
                          <AdCardEditButton
                            adName={ad.name}
                            field="campaign"
                            onSendMessage={onSendMessage}
                          />
                        </div>
                      ) : null}
                      {budget ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Wallet className="size-3.5 shrink-0" />
                          <span>{budget}</span>
                          <AdCardEditButton
                            adName={ad.name}
                            field="budget"
                            onSendMessage={onSendMessage}
                          />
                        </div>
                      ) : null}
                      {targeting ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <MapPin className="size-3.5 shrink-0" />
                          <span>{targeting}</span>
                          <AdCardEditButton
                            adName={ad.name}
                            field="targeting"
                            onSendMessage={onSendMessage}
                          />
                        </div>
                      ) : null}
                      {ad.destinationUrl && !ad.leadFormId ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <ExternalLink className="size-3.5 shrink-0" />
                          <span className="truncate">{ad.destinationUrl}</span>
                          <AdCardEditButton
                            adName={ad.name}
                            field="destination link"
                            onSendMessage={onSendMessage}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                /* The copy card's footprint while the ad loads, so the text
                   fills a box that is already there instead of pushing in. */
                <div
                  className="w-full max-w-sm space-y-2 rounded-lg border bg-card p-3"
                  aria-hidden="true"
                >
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdCardEditTargetContext.Provider>
  );
}
